/**
 * Drop Zone Component
 *
 * Drag-and-drop file upload zone for bug report attachments.
 * Provides visual feedback during drag operations and validates files.
 *
 * @module DropZone
 */

import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import type { FileRejection } from 'react-dropzone';

/**
 * Props for DropZone component
 */
export interface DropZoneProps {
  /** Callback when files are dropped and accepted */
  onFilesDrop: (files: File[]) => void;
  /** Whether the drop zone is disabled */
  disabled?: boolean;
  /** Maximum number of files allowed */
  maxFiles?: number;
  /** Class name for custom styling */
  className?: string;
}

/**
 * Drag-and-drop file upload zone
 *
 * Features:
 * - Visual feedback during drag operations (active, reject states)
 * - File validation (type, size) via react-dropzone
 * - Accessibility support (keyboard navigation, ARIA labels)
 * - Error display for rejected files
 * - Support for drag-and-drop and click-to-select
 *
 * @example
 * ```tsx
 * <DropZone
 *   onFilesDrop={(files) => {
 *     files.forEach(file => addAttachment(file, 'upload'));
 *   }}
 *   disabled={isSubmitting}
 *   maxFiles={10}
 * />
 * ```
 */
export const DropZone: React.FC<DropZoneProps> = ({
  onFilesDrop,
  disabled = false,
  maxFiles = 10,
  className = '',
}) => {
  /**
   * Handle drop event
   * Filters accepted files and passes them to parent callback
   */
  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      // Log rejected files for debugging
      if (rejectedFiles.length > 0) {
        console.warn('[DropZone] Files rejected:', {
          count: rejectedFiles.length,
          reasons: rejectedFiles.map((r) => ({
            file: r.file.name,
            errors: r.errors.map((e) => e.message),
          })),
        });
      }

      // Pass accepted files to parent
      if (acceptedFiles.length > 0) {
        console.info('[DropZone] Files accepted:', {
          count: acceptedFiles.length,
          files: acceptedFiles.map((f) => ({ name: f.name, size: f.size })),
        });
        onFilesDrop(acceptedFiles);
      }
    },
    [onFilesDrop]
  );

  /**
   * Configure react-dropzone
   * - Accept only image files
   * - Limit file size to 5MB (matches MAX_FILE_SIZE from constants)
   * - Disable when component is disabled
   * - Prevent opening the file dialog if clicking when disabled
   */
  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    disabled,
    maxFiles,
    // Accept all file types
    maxSize: 5 * 1024 * 1024, // 5MB
    noClick: disabled, // Prevent click when disabled
    noKeyboard: disabled, // Prevent keyboard when disabled
  });

  /**
   * Calculate visual state and styling
   */
  const getStateStyle = useCallback(() => {
    const baseStyle: React.CSSProperties = {
      border: '2px dashed #d1d5db',
      borderRadius: '8px',
      padding: '24px',
      textAlign: 'center',
      cursor: disabled ? 'not-allowed' : 'pointer',
      backgroundColor: '#f9fafb',
      transition: 'all 0.2s ease',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '12px',
    };

    if (disabled) {
      return {
        ...baseStyle,
        opacity: 0.5,
        backgroundColor: '#f3f4f6',
        borderColor: '#e5e7eb',
      };
    }

    if (isDragReject) {
      return {
        ...baseStyle,
        borderColor: '#ef4444',
        backgroundColor: '#fef2f2',
      };
    }

    if (isDragActive) {
      return {
        ...baseStyle,
        borderColor: '#3b82f6',
        backgroundColor: '#eff6ff',
      };
    }

    return baseStyle;
  }, [disabled, isDragActive, isDragReject]);

  /**
   * Get message based on state
   */
  const getMessage = useCallback(() => {
    if (disabled) {
      return 'File upload disabled';
    }

    if (isDragReject) {
      return 'File too large (max 5MB per file)';
    }

    if (isDragActive) {
      return 'Drop files here...';
    }

    return 'Drag & drop files here, or click to select';
  }, [disabled, isDragReject, isDragActive]);

  return (
    <div
      {...getRootProps()}
      className={`bug-reporter-dropzone ${className}`.trim()}
      style={getStateStyle()}
      role="button"
      aria-label="Upload attachments"
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      data-bug-reporter-dropzone
    >
      <input {...getInputProps()} aria-label="File input" />

      {/* Upload Icon */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          width: 48,
          height: 48,
          color: isDragReject ? '#ef4444' : isDragActive ? '#3b82f6' : '#6b7280',
          flexShrink: 0,
        }}
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>

      {/* Message */}
      <div
        style={{
          fontSize: 14,
          fontWeight: 500,
          color: isDragReject ? '#dc2626' : isDragActive ? '#1d4ed8' : '#374151',
        }}
      >
        {getMessage()}
      </div>

      {/* Helper text */}
      {!disabled && !isDragActive && !isDragReject && (
        <div
          style={{
            fontSize: 12,
            color: '#6b7280',
            marginTop: 4,
          }}
        >
          Any file type up to 5MB each (max {maxFiles} files)
        </div>
      )}

      {/* Error state indicator */}
      {isDragReject && (
        <div
          style={{
            fontSize: 12,
            color: '#dc2626',
            marginTop: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: 14, height: 14 }}
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          File too large (max 5MB)
        </div>
      )}
    </div>
  );
};
